/**
 * Validation utilities for forms and data
 */

/**
 * Validate phone number (Ghana format)
 */
export const validatePhoneNumber = (phone: string): { valid: boolean; error?: string } => {
    // Remove all non-digits
    const cleaned = phone.replace(/\D/g, '');

    // Should start with 233 and be 12 digits total
    if (!cleaned.startsWith('233')) {
        return { valid: false, error: 'Phone number must start with country code 233' };
    }

    if (cleaned.length !== 12) {
        return { valid: false, error: 'Phone number must be 12 digits (including country code)' };
    }

    return { valid: true };
};

/**
 * Validate email address
 */
export const validateEmail = (email: string): { valid: boolean; error?: string } => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(email)) {
        return { valid: false, error: 'Invalid email format' };
    }

    return { valid: true };
};

/**
 * Validate sender ID
 */
export const validateSenderId = (senderId: string): { valid: boolean; error?: string } => {
    if (!senderId || senderId.trim().length === 0) {
        return { valid: false, error: 'Sender ID is required' };
    }

    const cleaned = senderId.replace(/\s/g, '');

    if (!/^[a-zA-Z0-9]+$/.test(cleaned)) {
        return { valid: false, error: 'Sender ID must contain only letters and numbers' };
    }

    if (cleaned.length > 11) {
        return { valid: false, error: 'Sender ID must be 11 characters or less' };
    }

    return { valid: true };
};

/**
 * Validate campaign name
 */
export const validateCampaignName = (name: string): { valid: boolean; error?: string } => {
    if (!name || name.trim().length === 0) {
        return { valid: false, error: 'Campaign name is required' };
    }

    if (name.trim().length > 255) {
        return { valid: false, error: 'Campaign name must be 255 characters or less' };
    }

    return { valid: true };
};

/**
 * Validate message template
 */
export const validateMessageTemplate = (template: string): { valid: boolean; error?: string } => {
    if (!template || template.trim().length === 0) {
        return { valid: false, error: 'Message template is required' };
    }

    // SMS limit is typically 160 characters for single message
    // Allow up to 480 for concatenated messages (3 parts)
    if (template.length > 480) {
        return { valid: false, error: 'Message template is too long (max 480 characters)' };
    }

    return { valid: true };
};

/**
 * Validate file upload
 */
export const validateFileUpload = (file: File): { valid: boolean; error?: string } => {
    const maxSize = 10 * 1024 * 1024; // 10MB
    const allowedTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv',
    ];

    const allowedExtensions = ['.xlsx', '.xls', '.csv'];

    if (file.size > maxSize) {
        return { valid: false, error: 'File size exceeds 10MB limit' };
    }

    const fileName = file.name.toLowerCase();
    const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

    if (!hasValidExtension && !allowedTypes.includes(file.type)) {
        return { valid: false, error: 'Invalid file type. Please upload Excel (.xlsx, .xls) or CSV file' };
    }

    return { valid: true };
};

/**
 * Sanitize input to prevent XSS
 */
export const sanitizeInput = (input: string): string => {
    return input
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#x27;')
        .replace(/\//g, '&#x2F;');
};

/**
 * Validate scheduled date
 */
export const validateScheduledDate = (date: Date): { valid: boolean; error?: string } => {
    const now = new Date();

    if (date <= now) {
        return { valid: false, error: 'Scheduled date must be in the future' };
    }

    // Don't allow scheduling more than 1 year in advance
    const oneYearFromNow = new Date();
    oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);

    if (date > oneYearFromNow) {
        return { valid: false, error: 'Cannot schedule more than 1 year in advance' };
    }

    return { valid: true };
};